import * as React from 'react'
import Button from '@doordash/component-button'
import Form from '../../../utilities/Form'
import s from './strings'
import Toggle from '@doordash/component-toggle'
import { InlineChildren, StackChildren, Text } from '@doordash/design-language'
import { noop } from 'lodash'
import { Select, TextField } from '@doordash/component-fields'
import { useState } from 'react'
import { validators as sharedValidators } from '../../../utilities/validators'

const ALIAS_TYPES = [
  {
    name: s.alias(),
    value: 'ALIAS',
  },
  {
    name: s.maidenName(),
    value: 'MAIDN',
  },
  {
    name: s.otherName(),
    value: 'PREVS',
  },
]

export type PreviousAliasProps = {
  id: number
  type: string
  firstName: string
  middleName: string
  lastName: string
  isSingleName: boolean
  valid: boolean
}

type PreviousAliasComponentProps = {
  alias: PreviousAliasProps
  updateAliases: (id: number, data: PreviousAliasProps) => void
  removeAlias?: (id: number) => void
  /**
   * array of refs for storing references to all the alias forms,
   * this is used to submit all the forms from parent component.
   */
  refs?: React.RefObject<HTMLElement[]>
}

export const PreviousAlias: React.FC<PreviousAliasComponentProps> = ({
  alias,
  removeAlias,
  updateAliases,
  refs,
}) => {
  const [isSingleName, setIsSingleName] = useState<boolean>(alias.isSingleName)

  const fields = {
    type: alias.type,
    firstName: alias.firstName,
    lastName: alias.lastName,
    middleName: alias.middleName,
    isSingleName: alias.isSingleName,
  }

  const validators = {
    type: sharedValidators.alias.type,
    firstName: isSingleName ? noop : sharedValidators.alias.firstName,
    lastName: sharedValidators.alias.lastName,
    middleName: noop,
    isSingleName: noop,
  }

  const submit = async data => {
    updateAliases(alias.id, data)
  }

  return (
    <Form onSubmit={submit} validators={validators} fields={fields}>
      {(fields, handleChange, handleSubmit) => (
        <form onSubmit={handleSubmit} ref={el => (refs.current[alias.id] = el)}>
          <StackChildren>
            <Text styles={Text.Styles.Label2Emphasis}>Add Alias</Text>
            <Select
              description={s.required()}
              label={s.type()}
              onChange={value => {
                handleChange('type', value)
              }}
              options={ALIAS_TYPES}
              {...fields.type}
            />
            <Toggle
              onChange={value => {
                handleChange('isSingleName', value)
                setIsSingleName(value)
              }}
              isSelected={isSingleName}
              label={s.isSingleName()}
              {...fields.isSingleName}
            />
            {!isSingleName && (
              <>
                <TextField
                  description={s.required()}
                  onChange={value => {
                    handleChange('firstName', value)
                  }}
                  label={s.firstName()}
                  {...fields.firstName}
                />
                <TextField
                  onChange={value => {
                    handleChange('middleName', value)
                  }}
                  label={s.middleName()}
                  {...fields.middleName}
                />
              </>
            )}
            <TextField
              description={s.required()}
              onChange={value => {
                handleChange('lastName', value)
              }}
              label={isSingleName ? s.name() : s.lastName()}
              {...fields.lastName}
            />
            <InlineChildren justifyContent="flex-end">
              <Button
                onClick={() => removeAlias(alias.id)}
                isDisabled={false}
                type={Button.Types.Destructive}
                isInline={true}
              >
                {s.remove()}
              </Button>
            </InlineChildren>
          </StackChildren>
        </form>
      )}
    </Form>
  )
}
